import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useHistory, useParams } from 'react-router-dom';
import { Spin } from 'antd';
// Api
import { GET_TICKET_EVENT } from 'api/ticketing/queries';
// Types
import {
  GetTicketEvent,
  GetTicketEventVariables,
} from 'api/ticketing/types/GetTicketEvent';
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import { VenueMode } from 'pages/Events/components/TicketEventForm/types';
// Hooks
import { useUploadTicketEventImage } from 'pages/Events/components/TicketEventForm/useUploadTicketEventImage';
import { useWithSalesPaused } from 'pages/Events/hooks/useWithSalesPaused';
import { useTicketEventChanges } from 'pages/Events/hooks/useTicketEventChanges';
// Helpers
import {
  buildRescheduleInput,
  buildTierInput,
  buildUpdateDetailsInput,
  buildVenueInput,
  fitMaxAdmissions,
  getEditFormValues,
  getTierCapacityTotal,
} from 'pages/Events/components/TicketEventForm/helpers';
import { getTicketingErrorMessage } from 'pages/Events/helpers';
// Constants
import { TICKET_EVENT_DETAILS_GEN } from 'constants/routes';
// UI
import {
  errorNotification,
  successNotification,
  warningNotification,
} from 'ui/Notification';
// Components
import LoadErrorAlert from 'pages/Events/components/LoadErrorAlert/LoadErrorAlert';
import TicketEventForm, {
  TicketEventFormSubmit,
} from 'pages/Events/components/TicketEventForm/TicketEventForm';
// Styles
import styles from './EditTicketEvent.module.scss';

type EventDetails = TicketEventDetailsFields;

const EditTicketEvent = (): JSX.Element => {
  const history = useHistory();
  const { ticketEventId } = useParams<{ ticketEventId: string }>();
  const [submitting, setSubmitting] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const uploadImage = useUploadTicketEventImage();
  const withSalesPaused = useWithSalesPaused();
  const { updateDetails, reschedule, addTier, createVenue } =
    useTicketEventChanges();

  const { data, loading, error, refetch } = useQuery<
    GetTicketEvent,
    GetTicketEventVariables
  >(GET_TICKET_EVENT, {
    variables: {
      input: { ticketEventId },
    },
    fetchPolicy: 'network-only',
  });

  const event = data?.hostedTicketEvent;
  const savedTiers = useMemo(() => data?.hostedTicketTiers || [], [data]);
  const initialValues = useMemo(
    () => (event ? getEditFormValues(event) : null),
    [event]
  );

  const goToDetails = (): void =>
    history.push(`${TICKET_EVENT_DETAILS_GEN}/${ticketEventId}`);

  const handleSubmit = async ({
    values,
    timezone,
    coverImage,
  }: TicketEventFormSubmit): Promise<void> => {
    if (!event) {
      return;
    }

    setSubmitting(true);
    // Each change bumps the version the next one has to send
    let current: EventDetails = event;
    let partlySaved = false;
    const newTiers = values.ticketsEnabled ? values.tiers || [] : [];

    try {
      const coverImageFileKey = coverImage
        ? await uploadImage(coverImage)
        : null;
      let venueId = values.venueId;

      if (values.venueMode === VenueMode.New && values.venue) {
        venueId = await createVenue({
          ...buildVenueInput(values.venue),
          ownerId: event.hostUserId,
        });
        partlySaved = true;
      }

      const detailsInput = buildUpdateDetailsInput(
        event,
        {
          ...values,
          venueMode: VenueMode.Saved,
          venueId,
          // Raise the cap so the new tiers fit
          maxAdmissions: fitMaxAdmissions(
            values.maxAdmissions,
            getTierCapacityTotal(savedTiers) + getTierCapacityTotal(newTiers)
          ),
        },
        { coverImageFileKey }
      );

      if (detailsInput) {
        current = (await updateDetails(detailsInput)) || current;
        partlySaved = true;
      }

      const rescheduleInput = buildRescheduleInput(
        event,
        values,
        current.version,
        timezone
      );

      if (rescheduleInput) {
        current = (await reschedule(rescheduleInput)) || current;
        partlySaved = true;
      }

      if (newTiers.length) {
        await withSalesPaused(current, async () => {
          for (const tier of newTiers) {
            // One at a time so the display order follows the form
            // eslint-disable-next-line no-await-in-loop
            await addTier({ ...buildTierInput(tier), ticketEventId: event.id });
            partlySaved = true;
          }
        });
      }

      successNotification('The event is updated');
      goToDetails();
    } catch (err) {
      errorNotification(
        getTicketingErrorMessage(err, 'Failed to update the event')
      );
      setSubmitting(false);
      await refetch();

      // Show what the server has now, so a retry can't add the same tiers twice
      if (partlySaved) {
        setFormKey((key) => key + 1);
        warningNotification(
          'Part of the changes were saved before the error. The form now shows the saved event.'
        );
      }
    }
  };

  if (error) {
    return (
      <LoadErrorAlert
        message="Failed to load the event"
        error={error}
        onRetry={refetch}
      />
    );
  }

  if (loading || !event || !initialValues) {
    return (
      <div className={styles.loading}>
        <Spin />
      </div>
    );
  }

  return (
    <TicketEventForm
      key={`${event.id}-${formKey}`}
      mode="edit"
      event={event}
      savedTiers={savedTiers}
      initialValues={initialValues}
      submitting={submitting}
      onSubmit={handleSubmit}
      onCancel={goToDetails}
    />
  );
};

export default EditTicketEvent;
